import { Activity } from "react";
import cx from "classnames";
import { useShallow } from "zustand/react/shallow";

import Box from "ds/components/Box";

import useAssistantPanelStore from "./useStore";
import useSidebarResize from "./useSidebarResize";
import useAssistantPanelResponsiveVariant from "./hooks/useResponsiveVariant";
import styles from "./styles.module.css";
import useFloatingResize from "./useFloatingResize";

type AssistantPanelLayoutProps = {
  children: React.ReactNode;
};

const AssistantPanelLayout = ({ children }: AssistantPanelLayoutProps) => {
  useAssistantPanelResponsiveVariant();

  const { variant, isOpen, floatingPosition, floatingSize, isSidebarResizing } =
    useAssistantPanelStore(
      useShallow((state) => ({
        variant: state.variant,
        isOpen: state.isOpen,
        floatingPosition: state.floatingPosition,
        floatingSize: state.floatingSize,
        isSidebarResizing: state.isSidebarResizing,
      }))
    );

  const { onStart: onSidebarResizeStart } = useSidebarResize();
  const { onStart: onFloatingResizeStart } = useFloatingResize();

  const style =
    variant === "floating"
      ? {
          left: floatingPosition.x,
          top: floatingPosition.y,
          width: floatingSize.width,
          height: floatingSize.height,
        }
      : undefined;

  return (
    <Activity mode={isOpen ? "visible" : "hidden"}>
      <Box
        direction="column"
        className={cx(styles.wrapper, styles[variant])}
        style={style}
        data-react-aria-top-layer="true"
        data-ai-chat-layout={variant}
        fullWidth
      >
        {variant === "side" && (
          <div
            className={cx(styles.resizeBar, { [styles.resizing]: isSidebarResizing })}
            onPointerDown={onSidebarResizeStart}
            role="presentation"
          />
        )}

        {variant === "floating" && (
          <>
            <div
              className={styles.resizeN}
              onPointerDown={onFloatingResizeStart("n")}
              role="presentation"
            />
            <div
              className={styles.resizeS}
              onPointerDown={onFloatingResizeStart("s")}
              role="presentation"
            />
            <div
              className={styles.resizeE}
              onPointerDown={onFloatingResizeStart("e")}
              role="presentation"
            />
            <div
              className={styles.resizeW}
              onPointerDown={onFloatingResizeStart("w")}
              role="presentation"
            />
            {/* Corner handles */}
            <div
              className={styles.resizeNE}
              onPointerDown={onFloatingResizeStart("ne")}
              role="presentation"
            />
            <div
              className={styles.resizeNW}
              onPointerDown={onFloatingResizeStart("nw")}
              role="presentation"
            />
            <div
              className={styles.resizeSE}
              onPointerDown={onFloatingResizeStart("se")}
              role="presentation"
            />
            <div
              className={styles.resizeSW}
              onPointerDown={onFloatingResizeStart("sw")}
              role="presentation"
            />
          </>
        )}

        {children}
      </Box>
    </Activity>
  );
};

export default AssistantPanelLayout;
